import * as React from 'react';
import {
  H3Title,
  ItemRow,
  AddButton,
  Text,
  ItemContainer,
  RemoveButton,
  MarginB8,
} from 'pages/transactionManagement/style';
import PlusIcon from 'assets/images/plus';
import { useTranslation } from 'react-i18next';
import { ServiceSingleValue } from 'components/SingleValue';
import useAppState from 'hooks/useAppState';
import { TransactionService } from 'repositories/transaction';
import {
  useQueryGetOutletServices,
  OutletService,
  OutletStylist,
  OutletStylistSimple,
} from 'repositories/outlet';
import Select, { onSelect } from 'components/element/Select';
import { NewSelectDetail } from 'components/SelectDetail';
import PriceComponent from 'components/PriceComponent';

interface Props {
  selectedStylist?: OutletStylist;
  selectedStylistId?: string;
  values: TransactionService[];
  onChange?: (transactionServices: TransactionService[]) => void;
}

export interface ServiceOption {
  extra: OutletService;
}

export default function OutletServiceInput(props: Props) {
  const { values, onChange, selectedStylist, selectedStylistId } = props;
  const { t } = useTranslation();
  const { currentOutlet } = useAppState();
  const outletId = currentOutlet?.id || '';
  const [options, setOptions] = React.useState<ServiceOption[]>([]);
  const [
    selectedService,
    setSelectedService,
  ] = React.useState<ServiceOption | null>(null);

  useQueryGetOutletServices(
    outletId,
    React.useCallback((data) => {
      const serviceCategories = (data.outletServices.data || []).map(
        (item) => ({
          name: item.name,
          serviceCategoryId: item.serviceCategoryId,
          services: item.services,
        }),
      );

      setOptions(
        serviceCategories.map((category) => ({
          label: category.name,
          options: category.services.map((service) => ({
            label: service.name,
            value: service.id,
            extra: service,
          })),
        })),
      );
    }, []),
  );

  const getPrice = React.useCallback(
    ({
      stylists,
      defaultPrice,
      maxPrice,
    }: {
      stylists: OutletStylistSimple[];
      defaultPrice: any;
      maxPrice: number;
    }) => {
      if (selectedStylistId) {
        const stylist = stylists.find((s) => s.id === selectedStylistId);
        const price = Number(stylist?.price);

        if (stylist && price > 0) {
          return price;
        }

        return defaultPrice ?? maxPrice;
      }

      return defaultPrice;
    },
    [selectedStylistId],
  );

  function onServiceAdded() {
    if (!!selectedService) {
      const price = getPrice({
        stylists: selectedService.extra.stylists,
        defaultPrice: selectedService.extra.price,
        maxPrice: selectedService.extra.maxPrice,
      });

      console.log({ selectedService });

      onChange?.(
        values.concat({
          id: selectedService.extra.id,
          price,
          timeInMinutes: selectedService.extra.timeInMinutes,
          qty: 1,
          totalPrice: selectedService.extra.price,
          service: {
            id: selectedService.extra.id,
            name: selectedService.extra.name,
            imagePath: selectedService.extra.imagePath,
            price: selectedService.extra.price,
            minPrice: selectedService.extra.minPrice,
            maxPrice: selectedService.extra.maxPrice,
            minRegularPrice: selectedService.extra.minRegularPrice,
            maxRegularPrice: selectedService.extra.maxRegularPrice,
            timeInMinutes: selectedService.extra.timeInMinutes,
            point: selectedService.extra.point,
            isPriceList: selectedService.extra.isPriceList,
            regularPrice: selectedService.extra.regularPrice,
            stylists: selectedService.extra.stylists,
          },
        }),
      );
      setSelectedService(null);
    }
  }

  function onServiceRemoved(index: number) {
    onChange &&
      onChange(values.slice(0, index).concat(values.slice(index + 1)));
  }

  return (
    <>
      <H3Title>{t('common.service')}</H3Title>
      <>
        {values &&
          values.map((service, index) => {
            const stylistServices = selectedStylist?.service ?? [];
            const matchedService = stylistServices.find(
              (s) => s.id === service.service.id,
            );

            const fixedPrice =
              getPrice({
                stylists: service.service.stylists ?? [],
                defaultPrice: matchedService?.price,
                maxPrice: service.service.maxPrice,
              }) ?? 0;

            return (
              <ItemContainer>
                <ItemRow>
                  <RemoveButton onClick={() => onServiceRemoved(index)}>
                    -
                  </RemoveButton>
                  <Text>{service.service.name}</Text>
                </ItemRow>
                <PriceComponent
                  {...{
                    price: service.price,
                    isPriceList: service.service.isPriceList,
                    regularPrice: service.service.regularPrice,
                    isSelected: false,
                    minPrice: service.service.minPrice,
                    maxPrice: service.service.maxPrice,
                    minRegularPrice: service.service.minRegularPrice,
                    maxRegularPrice: service.service.maxRegularPrice,
                    fixedPrice,
                  }}
                />
              </ItemContainer>
            );
          })}
      </>
      <Select<ServiceOption>
        isMulti={false}
        options={options}
        components={{ SingleValue: ServiceSingleValue }}
        formatOptionLabel={NewSelectDetail}
        value={selectedService}
        onChange={onSelect(({ singleValue }) => {
          setSelectedService(singleValue || null);
        })}
        placeholder={t('booking.service_type')}
        styles={{
          groupHeading: (base) => ({
            ...base,
            fontWeight: 'bold',
            color: 'black',
            fontSize: 14,
          }),
        }}
      />
      <MarginB8 />
      <AddButton type="button" onClick={onServiceAdded}>
        <PlusIcon width={25} height={25} />
        <Text>{t('common.service')}</Text>
      </AddButton>
    </>
  );
}
